import { useState } from 'react';
import { AlertTriangle, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { supabase, isAuthConfigured } from '../../../lib/supabase';
import { useAuthStore } from '../../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';

// ── Reauth gate ───────────────────────────────────────────────────────────────

interface ReauthGateProps {
  onConfirmed: () => void;
  onCancel: () => void;
  action: string;
}

function ReauthGate({ onConfirmed, onCancel, action }: ReauthGateProps) {
  const { user } = useAuthStore();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  async function handleConfirm() {
    if (!password) return;
    setChecking(true);
    setError('');
    try {
      if (isAuthConfigured && user?.email) {
        const { error: authError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password,
        });
        if (authError) {
          setError('Password is incorrect');
          return;
        }
      }
      onConfirmed();
    } catch {
      setError('Authentication failed');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="op-panel p-4 space-y-4 border-loss/40 bg-loss/5">
      <p className="text-sm text-gray-300">
        To {action}, confirm your password:
      </p>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Your current password"
        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
        className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-loss focus:ring-1 focus:ring-loss"
      />
      {error && (
        <p className="text-xs text-loss flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={handleConfirm}
          disabled={checking || !password}
          className="btn-danger flex items-center gap-2 disabled:opacity-50"
        >
          {checking && <Loader2 className="w-4 h-4 animate-spin" />}
          Confirm
        </button>
        <button onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AccountSettingsPage() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();

  // Deactivation state
  const [deactivateStep, setDeactivateStep] = useState<'idle' | 'confirm' | 'reauth' | 'done'>('idle');
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  // Deletion state
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'reauth' | 'final' | 'done'>('idle');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  async function handleDeactivate() {
    setDeactivating(true);
    setDeactivateError('');
    try {
      // Soft-deactivation: sign out the user (account remains but they must sign in again)
      await signOut();
      setDeactivateStep('done');
      navigate('/login');
    } catch (err: unknown) {
      setDeactivateError(err instanceof Error ? err.message : 'Failed to deactivate account');
    } finally {
      setDeactivating(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError('');
    try {
      // In production, this would call a server-side endpoint that deletes the account
      // For now, sign the user out to end their session
      await signOut();
      setDeleteStep('done');
      navigate('/');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Account</h2>
        <p className="text-sm text-gray-500 mt-1">Manage your account status and data.</p>
      </div>

      {/* Account Info */}
      <div className="op-panel p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Account Overview</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2 border-b border-surface-4">
            <span className="text-sm text-gray-400">Email</span>
            <span className="text-sm text-gray-200">{user?.email ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-surface-4">
            <span className="text-sm text-gray-400">User ID</span>
            <span className="text-xs font-mono text-gray-500">{user?.id?.slice(0, 8)}…</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-400">Status</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-win/10 text-win border border-win/20">
              <CheckCircle className="w-3 h-3" /> Active
            </span>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="rounded-lg border border-loss/30 overflow-hidden">
        <div className="px-6 py-4 bg-loss/5 border-b border-loss/20">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-loss" />
            <h3 className="text-sm font-semibold text-loss">Danger Zone</h3>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Deactivate */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="text-sm font-medium text-gray-200">Deactivate Account</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Temporarily disable your account. You can reactivate by signing in again.
                </p>
              </div>
              {deactivateStep === 'idle' && (
                <button
                  onClick={() => setDeactivateStep('confirm')}
                  className="btn-ghost text-sm flex-shrink-0 border border-surface-4"
                >
                  Deactivate
                </button>
              )}
            </div>

            {deactivateStep === 'confirm' && (
              <div className="op-panel p-4 space-y-3 border-warn/30 bg-warn/5">
                <p className="text-sm text-gray-300">
                  Are you sure you want to deactivate your account? You can sign in again to reactivate it.
                </p>
                {deactivateError && (
                  <p className="text-xs text-loss">{deactivateError}</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDeactivateStep('reauth')}
                    className={clsx('btn-danger flex items-center gap-2', deactivating && 'opacity-50')}
                  >
                    {deactivating && <Loader2 className="w-4 h-4 animate-spin" />}
                    Continue
                  </button>
                  <button
                    onClick={() => setDeactivateStep('idle')}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {deactivateStep === 'reauth' && (
              <ReauthGate
                action="deactivate your account"
                onConfirmed={handleDeactivate}
                onCancel={() => setDeactivateStep('idle')}
              />
            )}
          </div>

          <div className="border-t border-loss/20" />

          {/* Delete */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="text-sm font-medium text-loss">Delete Account</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              {deleteStep === 'idle' && (
                <button
                  onClick={() => setDeleteStep('confirm')}
                  className="btn-danger text-sm flex-shrink-0"
                >
                  Delete Account
                </button>
              )}
            </div>

            {deleteStep === 'confirm' && (
              <div className="op-panel p-4 space-y-4 border-loss/40 bg-loss/5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-200">
                    This will permanently delete:
                  </p>
                  <ul className="text-xs text-gray-500 space-y-0.5 list-disc list-inside">
                    <li>Your profile and all personal data</li>
                    <li>Your betting history and analytics</li>
                    <li>Your organization memberships</li>
                    <li>All preferences and settings</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1.5">
                    Type <strong className="text-gray-200">DELETE</strong> to confirm:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full px-3 py-2 bg-surface-3 border border-loss/40 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-loss focus:ring-1 focus:ring-loss"
                  />
                </div>
                {deleteError && (
                  <p className="text-xs text-loss">{deleteError}</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => deleteConfirmText === 'DELETE' && setDeleteStep('reauth')}
                    disabled={deleteConfirmText !== 'DELETE'}
                    className="btn-danger flex items-center gap-2 disabled:opacity-40"
                  >
                    Continue to Verification
                  </button>
                  <button
                    onClick={() => { setDeleteStep('idle'); setDeleteConfirmText(''); }}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {deleteStep === 'reauth' && (
              <ReauthGate
                action="permanently delete your account"
                onConfirmed={() => setDeleteStep('final')}
                onCancel={() => setDeleteStep('idle')}
              />
            )}

            {deleteStep === 'final' && (
              <div className="op-panel p-4 space-y-3 border-loss/40 bg-loss/5">
                <p className="text-sm text-gray-200 font-medium">
                  Final confirmation: this cannot be undone.
                </p>
                {deleteError && (
                  <p className="text-xs text-loss">{deleteError}</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="btn-danger flex items-center gap-2 disabled:opacity-50"
                  >
                    {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Permanently Delete Account
                  </button>
                  <button
                    onClick={() => setDeleteStep('idle')}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
